import type { CCPreset } from '~/types/presets';
import type { MidiCC, DeviceProfile } from '~/types/midi';

/**
 * "Rumbles" CC preset for YO! FM Synth
 *
 * This is a backup of the original Init preset before applying official Tone.js settings.
 * Kept for reference and as an alternative preset option.
 */

const rumblesFMSynthCCs: MidiCC[] = [
  { number: 1, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 7, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 11, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 14, value: 50, scramble: true, smooth: false, show: true, min: 0, max: 127 }, // Reduced modulation index for less harshness
  { number: 15, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 }, // More harmonic ratio (closer to 2:1)
  { number: 16, value: 20, scramble: true, smooth: false, show: true, min: 0, max: 100 }, // Slightly slower mod env attack
  { number: 17, value: 100, scramble: true, smooth: false, show: true, min: 5, max: 127 },
  { number: 18, value: 50, scramble: true, smooth: false, show: true, min: 20, max: 127 }, // Lowered mod env sustain for warmer sound
  { number: 19, value: 120, scramble: true, smooth: false, show: true, min: 5, max: 127 },
  { number: 70, value: 90, scramble: true, smooth: false, show: true, min: 0, max: 127 }, // Increased carrier sustain for fuller sound
  { number: 71, value: 35, scramble: false, smooth: false, show: true, min: 0, max: 127 }, // Added body/warmth
  { number: 72, value: 85, scramble: true, smooth: false, show: true, min: 10, max: 127 }, // Reduced brightness
  { number: 73, value: 65, scramble: true, smooth: false, show: true, min: 3, max: 80 }, // Slightly longer carrier attack softens the onset
  { number: 74, value: 80, scramble: true, smooth: false, show: true, min: 0, max: 127 }, // Lowered filter cutoff for warmer tone
  { number: 75, value: 40, scramble: true, smooth: false, show: true, min: 10, max: 127 }, // Slightly longer carrier decay
  { number: 76, value: 32, scramble: true, smooth: false, show: true, min: 0, max: 127 }, // Square wave carrier for more fundamental
];

/**
 * Device profile for "Rumbles" FM Synth preset
 */
const rumblesFMSynthProfile: DeviceProfile = {
  id: 'yo-fm-synth-rumbles',
  name: 'Rumbles',
  deviceName: 'YO! FM Synth',
  userId: null, // System profile
  isPublic: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  ccConfig: [
    {
      number: 1,
      enabled: false,
      min: 0,
      max: 127,
      label: 'Modulation',
    },
    {
      number: 7,
      enabled: false,
      min: 80,
      max: 110,
      label: 'Volume',
    },
    {
      number: 11,
      enabled: false,
      min: 80,
      max: 110,
      label: 'Expression',
    },
    {
      number: 14,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Modulation Index',
    },
    {
      number: 15,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Harmonicity',
    },
    {
      number: 16,
      enabled: false, // Disabled - mod env attack kept stable
      min: 0,
      max: 100,
      label: 'Mod Env Attack',
    },
    {
      number: 17,
      enabled: true, // ✅ Mod Env Decay
      min: 5,
      max: 127,
      label: 'Mod Env Decay',
    },
    {
      number: 18,
      enabled: true, // ✅ Mod Env Sustain
      min: 20,
      max: 127,
      label: 'Mod Env Sustain',
    },
    {
      number: 19,
      enabled: true, // ✅ Mod Env Release
      min: 5,
      max: 127,
      label: 'Mod Env Release',
    },
    {
      number: 70,
      enabled: true, // ✅ Carrier Sustain
      min: 0,
      max: 127,
      label: 'Carrier Sustain',
    },
    {
      number: 72,
      enabled: false, // Disabled - keep preset release time
      min: 10,
      max: 127,
      label: 'Carrier Release',
    },
    {
      number: 73,
      enabled: false, // Disabled - keep preset attack time
      min: 3,
      max: 80,
      label: 'Carrier Attack',
    },
    {
      number: 74,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Modulator Type',
    },
    {
      number: 75,
      enabled: true,
      min: 10,
      max: 127,
      label: 'Carrier Decay',
    },
    {
      number: 76,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Carrier Type',
    },
  ],
};

/**
 * "Rumbles" FM Synth CC Preset
 */
export const RUMBLES_FM_SYNTH_PRESET: CCPreset = {
  id: 'rumbles-fm',
  name: 'Rumbles',
  deviceType: 'FMSynth',
  cc: rumblesFMSynthCCs,
  profiles: [rumblesFMSynthProfile],
  activeProfileId: 'yo-fm-synth-rumbles',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the "Rumbles" FM Synth CC preset
 * Returns a deep copy to avoid mutation
 */
export function getRumblesFMSynthPreset(): CCPreset {
  return {
    ...RUMBLES_FM_SYNTH_PRESET,
    cc: RUMBLES_FM_SYNTH_PRESET.cc.map(cc => ({ ...cc })),
    profiles: RUMBLES_FM_SYNTH_PRESET.profiles.map(profile => ({
      ...profile,
      ccConfig: profile.ccConfig.map(config => ({ ...config })),
    })),
  };
}
